import { Spaces } from '@ff-os/rendering-shared/coordinates';
import { getFilterBackend } from '@ff-os/rendering-shared/photo-filters/filterBackend';
import { logWarning } from '@ff-os/shared-utils/logging';
import { Translate } from '@ff-os/shared-utils/types/Translate';
import type { PhotoPrintsPdpDispatch } from '@ff-scene-plugins/photo-prints-redux/store';
import * as ff from 'ff-canvas';
import { createDOMCanvas, Device, Paintable, Pipeline, Transform } from 'ff-canvas';
import { loadImage } from './asset-loading/loadImage';
import { loadShape } from './asset-loading/loadShape';
import { loadSvg } from './asset-loading/loadSvg';
import { createMagicBorderPlugin } from './magicBorderPlugin';
import { drawPhotoPlaceholder } from './photo-placeholder/drawPhotoPlaceholder';
import { drawLoadingPlaceholderFor } from './placeholder/drawLoadingPlaceholderFor';

type Options = {
    requestRender: () => void;
    transform: Transform;
    useDevicePixelRatio: boolean;
    spaces?: Spaces;
    dispatch?: PhotoPrintsPdpDispatch;
};

const MAX_CHROME_SURFACE = {
    width: 32_767,
    height: 32_767,
    area: 268_435_456
};

export const createPipeline = (
    canvas: HTMLCanvasElement,
    translation: { t: Translate; language: string },
    options: Options
): Pipeline => {

    const device = options.useDevicePixelRatio
        ? Device.browser(MAX_CHROME_SURFACE, options.transform)
        : new Device(createDOMCanvas, MAX_CHROME_SURFACE, 1, options.transform);

    const pipeline = new Pipeline(
        canvas, device,
        options.requestRender,
        e => logWarning(e),
        [createMagicBorderPlugin(options.dispatch!)]);

    if (options.useDevicePixelRatio) {
        pipeline.applyPixelRatio();
    }

    addPaintersTo(pipeline, translation.t, translation.language);

    return pipeline;
};

const addPaintersTo = (
    pipeline: Pipeline,
    t: Translate,
    language: string
): void => {

    const getLoaderPlaceholder = (object: Paintable) => drawLoadingPlaceholderFor(object, pipeline);

    const painters = [

        new ff.RectanglePainter(),
        new ff.CirclePainter(),
        new ff.TrianglePainter(),
        new ff.CustomShapePainter(),
        new ff.LoadableShapePainter(bindTo(language, loadShape)),

        new ff.TextPainter('browser'),
        new ff.QrCodePainter(),

        new ff.PhotoPainter(
            bindTo(language, loadImage),
            getLoaderPlaceholder,
            (photo) => drawPhotoPlaceholder(pipeline, t, photo),
            getFilterBackend(createDOMCanvas)),

        new ff.ClipartPainter(bindTo(language, loadImage), getLoaderPlaceholder),
        new ff.SvgPainter(bindTo(language, loadSvg), bindTo(language, loadImage), getLoaderPlaceholder),
        new ff.PatternPainter(bindTo(language, loadImage), getLoaderPlaceholder)
    ];

    for (const painter of painters) {
        pipeline.use(painter);
    }
};

const bindTo = <T>(
    language: string,
    load: (url: string, language: string) => Promise<T>
) => {
    return (url: string) => load(url, language);
};